import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";

interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: any;
}

interface IAddTag {
  tag_name: string;
}

interface IUpdateHouseStatusSetting {
  reservation_style?: number;
  reservation_staying_status_rule?: number;
  house_status_page_size?: number;
  house_price_page_size?: number;
}

interface IRemoveTag {
  operator_tag_id: number;
}

interface IAddClientTag {
  name: string;
}

interface IRemoveClientTag {
  client_tag_id: number;
}

interface IAddChannel {
  channel_name: string;
  bg_color: string;
}

interface ISetChannelColor {
  code: number;
  bg_color: string;
  name: string;
}

interface IAddIncomeItem {
  item_name: string;
}

interface IAddOutcomeItem {
  item_name: string;
}

interface IAddIncomeMethod {
  method_name: string;
}

interface IAddOutcomeMethod {
  method_name: string;
}

interface IRemoveChannel {
  reservation_channel_code: number;
}

interface IRemoveIncomeItem {
  income_item_code: number;
}

interface IRemoveOutcomeItem {
  outcome_item_code: number;
}

interface IRemoveIncomeMethod {
  income_method_code: number;
}

interface IRemoveOutcomeMethod {
  outcome_method_code: number;
}

interface ISortChannel {
  sorted_codes: string;
}

const ADD_TAG_API = "/api/house_status_setting/add_tag";
const GET_SETTINGS_API = "/api/house_status_setting/get";
const UPDATE_HOUSE_STATUS_SETTING_API = "/api/house_status_setting/update";
const REMOVE_TAG_API = "/api/house_status_setting/remove_tag";
const ADD_CHANNEL_API = "/api/house_status_setting/add_channel";
const ADD_HOUSE_TAG_API = "/api/house_status_setting/add_house_tag";
const SET_CHANNEL_COLOR_API = "/api/house_status_setting/set_channel_color";
const ADD_INCOME_ITEM_API = "/api/house_status_setting/add_income_item";
const ADD_OUTCOME_ITEM_API = "/api/house_status_setting/add_outcome_item";
const ADD_INCOME_METHOD_API = "/api/house_status_setting/add_income_method";
const ADD_OUTCOME_METHOD_API = "/api/house_status_setting/add_outcome_method";
const REMOVE_CHANNEL_API = "/api/house_status_setting/remove_channel";
const REMOVE_INCOME_ITEM_API = "/api/house_status_setting/remove_income_item";
const REMOVE_OUTCOME_ITEM_API = "api/house_status_setting/remove_outcome_item";
const REMOVE_INCOME_METHOD_API =
  "api/house_status_setting/remove_income_method";
const REMOVE_OUTCOME_METHOD_API =
  "api/house_status_setting/remove_outcome_method";
const REFRESH_ALL_STAYING_STATUS_API =
  "/api/reservation/refresh_all_staying_status";
const ADD_CLIENT_TAG_API = "/api/house_status_setting/add_client_tag";
const REMOVE_CLIENT_TAG_API = "/api/house_status_setting/remove_client_tag";
const REMOVE_HOUSE_TAG_API = "/api/house_status_setting/remove_house_tag";
const SORT_CHANNEL_API = "/api/house_status_setting/sort_channel";

@Injectable({
  providedIn: "root",
})
export class HouseStatusSettingService {
  constructor(private http: HttpClient) {}

  /** 获取房态配置 */
  getSettings() {
    const query = new HttpParams();
    return this.http.get(GET_SETTINGS_API, { params: query }) as Observable<
      IHttpResponse
    >;
  }

  /** 添加标签 */
  addTag(params: IAddTag) {
    return this.http.post(ADD_TAG_API, params) as Observable<IHttpResponse>;
  }

  /** 修改房态配置 */
  updateHouseStatusSetting(params: IUpdateHouseStatusSetting) {
    return this.http.post(
      UPDATE_HOUSE_STATUS_SETTING_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 添加房态配置来源渠道 */
  addChannel(params: IAddChannel) {
    return this.http.post(ADD_CHANNEL_API, params) as Observable<IHttpResponse>;
  }

  /** 设置渠道颜色 */
  setChannelColor(params: ISetChannelColor) {
    return this.http.post(SET_CHANNEL_COLOR_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 添加房态配置收入项目 */
  addIncomeItem(params: IAddIncomeItem) {
    return this.http.post(ADD_INCOME_ITEM_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 添加房态配置支出项目 */
  addOutcomeItem(params: IAddOutcomeItem) {
    return this.http.post(ADD_OUTCOME_ITEM_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 添加房态配置收入交易方式 */
  addIncomeMethod(params: IAddIncomeMethod) {
    return this.http.post(ADD_INCOME_METHOD_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 添加房态配置支出交易方式 */
  addOutcomeMethod(params: IAddOutcomeMethod) {
    return this.http.post(ADD_OUTCOME_METHOD_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 移除房态配置标签 */
  removeTag(params: IRemoveTag) {
    return this.http.post(REMOVE_TAG_API, params) as Observable<IHttpResponse>;
  }

  /** 移除房态配置来源渠道 */
  removeChannel(params: IRemoveChannel) {
    return this.http.post(REMOVE_CHANNEL_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 移除房态配置收入项目 */
  removeIncomeItem(params: IRemoveIncomeItem) {
    return this.http.post(REMOVE_INCOME_ITEM_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 移除房态配置支出项目 */
  removeOutcomeItem(params: IRemoveOutcomeItem) {
    return this.http.post(REMOVE_OUTCOME_ITEM_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 移除房态配置收入交易方式 */
  removeIncomeMethod(params: IRemoveIncomeMethod) {
    return this.http.post(REMOVE_INCOME_METHOD_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 移除房态配置支出交易方式 */
  removeOutcomeMethod(params: IRemoveOutcomeMethod) {
    return this.http.post(REMOVE_OUTCOME_METHOD_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 刷新入住状态 */
  refreshAllStayingStatus() {
    return this.http.post(REFRESH_ALL_STAYING_STATUS_API, {}) as Observable<
      IHttpResponse
    >;
  }

  /** 添加房客标签 */
  addGuestTag(params: IAddClientTag) {
    return this.http.post(ADD_CLIENT_TAG_API, params) as Observable<
      IHttpResponse
    >;
  }

  /** 移除房客标签 */
  removeGuestTag(params: IRemoveClientTag) {
    return this.http.post(REMOVE_CLIENT_TAG_API, params) as Observable<
      IHttpResponse
    >;
  }

  addHouseTag (params) {
    return this.http.post(ADD_HOUSE_TAG_API, params) as Observable<IHttpResponse>;
  }

  removeHouseTag (params) {
    return this.http.post(REMOVE_HOUSE_TAG_API, params) as Observable<IHttpResponse>;
  }

  /** 排序渠道 */
  sortChannel(params: ISortChannel) {
    return this.http.post(SORT_CHANNEL_API, params) as Observable<
      IHttpResponse
    >;
  }
}
